/**
 * GSAP Snowflakes Animation
 * Creates falling snowflakes across the viewport using GSAP.
 * Snowflakes are initialized at random positions throughout the viewport
 * so they appear to already be falling when the page loads.
 */

// Animation constants
const MIN_DURATION = 10;
const MAX_DURATION = 18;
const PERSPECTIVE = 600;

class GsapSnowflakes extends HTMLElement {
  constructor() {
    super();
    this.snowflakes = [];
    this.loadHandler = null;
  }

  connectedCallback() {
    if (typeof gsap === "undefined") {
      // Check if load already fired
      if (document.readyState === "complete") {
        this.init();
      } else {
        this.loadHandler = () => this.init();
        window.addEventListener("load", this.loadHandler);
      }
    } else {
      this.init();
    }
  }

  disconnectedCallback() {
    this.cleanup();
  }

  init() {
    if (typeof gsap === "undefined") return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const container = this.querySelector('[ref="snowflakes"]');
    if (!container) return;

    this.container = container;
    this.count = parseInt(this.dataset.count, 10) || 30;
    this.images = JSON.parse(this.dataset.images || "[]");

    if (this.images.length === 0) return;

    gsap.set(this.container, { perspective: PERSPECTIVE });
    this.createSnowflakes();
  }

  createSnowflakes() {
    for (let i = 0; i < this.count; i++) {
      this.createSnowflake();
    }
  }

  createSnowflake() {
    const w = window.innerWidth;
    const h = window.innerHeight;

    const snowflake = document.createElement("div");
    snowflake.className = "snowflake";

    const img = document.createElement("img");
    img.src = this.images[randomInt(0, this.images.length - 1)];
    img.alt = "";
    img.loading = "lazy";
    img.decoding = "async";
    snowflake.appendChild(img);

    // Random size class
    const sizeClass = ["snowflake--sm", "snowflake--md", "snowflake--lg"][
      randomInt(0, 2)
    ];
    snowflake.classList.add(sizeClass);

    // Initial position - spread throughout viewport for natural "already falling" look
    const startY = randomInt(-100, h);
    const startX = randomInt(0, w);

    gsap.set(snowflake, {
      x: startX,
      y: startY,
      z: randomInt(-200, 200),
      xPercent: -50,
      yPercent: -50,
      rotation: randomInt(-30, 30),
    });

    this.container.appendChild(snowflake);
    this.snowflakes.push(snowflake);

    // Start animation immediately from current position
    this.animateSnowflake(snowflake, startY);
  }

  animateSnowflake(snowflake, currentY) {
    const w = window.innerWidth;
    const h = window.innerHeight;

    // Calculate remaining distance to bottom
    const remainingDistance = h + 100 - currentY;
    const fullDistance = h + 200; // Full journey from top to bottom

    // Duration proportional to remaining distance
    const fullDuration = randomFloat(MIN_DURATION, MAX_DURATION);
    const duration = (remainingDistance / fullDistance) * fullDuration;

    // Gentle horizontal drift
    const drift = randomInt(-80, 80);

    gsap.to(snowflake, {
      duration: Math.max(duration, 2), // Minimum 2 seconds
      y: h + 100,
      x: `+=${drift}`,
      rotation: `+=${randomInt(-45, 45)}`,
      ease: "none",
      onComplete: () => this.resetSnowflake(snowflake),
    });
  }

  resetSnowflake(snowflake) {
    const w = window.innerWidth;

    // Reset to top, random X position
    gsap.set(snowflake, {
      x: randomInt(0, w),
      y: randomInt(-150, -50),
      z: randomInt(-200, 200),
      rotation: randomInt(-30, 30),
    });

    // Animate full journey
    this.animateFullJourney(snowflake);
  }

  animateFullJourney(snowflake) {
    const w = window.innerWidth;
    const h = window.innerHeight;

    const duration = randomFloat(MIN_DURATION, MAX_DURATION);
    const drift = randomInt(-80, 80);

    gsap.to(snowflake, {
      duration,
      y: h + 100,
      x: `+=${drift}`,
      rotation: `+=${randomInt(-45, 45)}`,
      ease: "none",
      onComplete: () => this.resetSnowflake(snowflake),
    });
  }

  cleanup() {
    if (this.loadHandler) {
      window.removeEventListener("load", this.loadHandler);
      this.loadHandler = null;
    }

    for (const snowflake of this.snowflakes) {
      gsap.killTweensOf(snowflake);
      snowflake.remove();
    }
    this.snowflakes = [];
  }
}

const randomInt = (min, max) =>
  Math.floor(Math.random() * (max - min + 1)) + min;
const randomFloat = (min, max) => Math.random() * (max - min) + min;

if (!customElements.get("gsap-snowflakes")) {
  customElements.define("gsap-snowflakes", GsapSnowflakes);
}
